/**
 * useIntentSignal.ts — SSE hook cho workspace AI intent signals.
 *
 * CR-WS-019 | TASK-UI-WS019-002 + TASK-UI-WS019-008
 *
 * Pattern: exponential backoff reconnect (giống useAgentEvents).
 * SSE URL: /api/v1/workspaces/:id/events?types=intent_signal
 * Event: { type: "intent_signal", intent, confidence, method }
 */

import { useEffect, useRef, useCallback, useState } from "react";
import type { AIMode } from "@/features/chat/components/ModeSelector";

const MAX_RETRY_DELAY_MS = 30_000;
const BASE_RETRY_DELAY_MS = 2_000;

export interface IntentSignalEvent {
  intent: AIMode;
  confidence: number;    // 0–1
  method: "regex" | "llm" | "hybrid";
  sessionId?: string;
}

export interface UseIntentSignalOptions {
  workspaceId?: string;
  conversationId?: string;
  sessionId?: string;
  enabled?: boolean;
}

export interface UseIntentSignalResult {
  intentSignal: IntentSignalEvent | null;
  signal: IntentSignalEvent | null;
  isThinking: boolean;
  isConnected: boolean;
  clearSignal: () => void;
}

export function useIntentSignal(
  optionsOrId: string | UseIntentSignalOptions
): UseIntentSignalResult {
  const options = typeof optionsOrId === "string" ? { workspaceId: optionsOrId } : optionsOrId;
  const { workspaceId = options.conversationId || "", sessionId, enabled = true } = options;
  const [intentSignal, setIntentSignal] = useState<IntentSignalEvent | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCount = useRef(0);

  const clearSignal = useCallback(() => setIntentSignal(null), []);

  const connect = useCallback(() => {
    if (!workspaceId || !enabled) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const params = new URLSearchParams({ types: "intent_signal" });
    if (sessionId) params.set("session_id", sessionId);

    fetch(
      `/api/v1/workspaces/${workspaceId}/events?${params}`,
      {
        signal: ctrl.signal,
        headers: { Accept: "text/event-stream" },
      }
    )
      .then(async (res) => {
        if (!res.ok || !res.body) {
          throw new Error(`SSE ${res.status}`);
        }
        setIsConnected(true);
        retryCount.current = 0;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const payload = JSON.parse(line.slice(6));
                if (payload.type === "intent_signal") {
                  setIntentSignal({
                    intent: payload.intent,
                    confidence: payload.confidence ?? 1,
                    method: payload.method ?? "llm",
                    sessionId: payload.session_id,
                  });
                  // Auto-clear signal after 8s (intent is ephemeral)
                  setTimeout(() => setIntentSignal(null), 8_000);
                }
              } catch {
                // Malformed JSON — skip
              }
            }
          }
        }
      })
      .catch((err: unknown) => {
        if ((err as { name?: string }).name === "AbortError") return;
        setIsConnected(false);

        // Exponential backoff reconnect
        const delay = Math.min(
          BASE_RETRY_DELAY_MS * 2 ** retryCount.current,
          MAX_RETRY_DELAY_MS
        );
        retryCount.current += 1;
        retryTimerRef.current = setTimeout(connect, delay);
      });
  }, [workspaceId, sessionId, enabled]);

  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => {
      abortRef.current?.abort();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      setIsConnected(false);
    };
  }, [connect, enabled]);

  return {
    intentSignal,
    signal: intentSignal,
    isThinking: intentSignal !== null,
    isConnected,
    clearSignal,
  };
}

/**
 * Local intent prediction fallback (regex-based).
 * Dùng khi SSE không có signal.
 * Per SOLUTION.md §3.6 heuristic table.
 */
export function predictIntentLocally(text: string): IntentSignalEvent {
  const t = text.toLowerCase();

  if (/\b(write|create|build|implement|code|function|class|component)\b/.test(t)) {
    return { intent: "code", confidence: 0.7, method: "regex" };
  }
  if (/\b(plan|strategy|roadmap|outline|steps|milestones)\b/.test(t)) {
    return { intent: "plan", confidence: 0.7, method: "regex" };
  }
  if (/\b(research|find|search|look up|what is|explain|how does)\b/.test(t)) {
    return { intent: "research", confidence: 0.65, method: "regex" };
  }
  return { intent: "chat", confidence: 0.5, method: "regex" };
}
